import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createMarketplaceCheckout,
  getMarketplaceCheckoutContext,
} from "../../api/myboothshareBilling";
import {
  acknowledgeRentalTerms,
  acknowledgeRentalWarning,
  getRentalByBooking,
} from "../../api/workspaceRentals";
import RenterAcceptanceSummaryCard from "../../components/rentals/RenterAcceptanceSummaryCard";
import RenterAgreementAcceptanceSection from "../../components/rentals/RenterAgreementAcceptanceSection";
import RenterWarningsPanel from "../../components/rentals/RenterWarningsPanel";
import { getPublicWorkspace } from "../../api/workspaceMarketplace";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "./MarketplaceShell";
import {
  formatWorkspaceMoney,
  getWorkspaceDisplayName,
  getWorkspaceListingPath,
  titleCaseLabel,
} from "../../utils/workspacePresentation";

function defaultBookingWindow() {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 2);
  const end = new Date(start);
  end.setHours(end.getHours() + 4);
  return {
    start_at_utc: start.toISOString().slice(0, 16),
    end_at_utc: end.toISOString().slice(0, 16),
  };
}

function normalizeAddon(addon, index) {
  const key = addon?.key || addon?.code || `addon-${index + 1}`;
  const label = addon?.label || addon?.name || addon?.title || "Add-on";
  const pricing_type = addon?.pricing_type || addon?.pricingType || "flat";
  const unit_price = Number(
    addon?.unit_price ?? addon?.unitPrice ?? addon?.price ?? 0
  );
  return {
    key,
    label,
    pricing_type,
    unit_price,
    quantity: 1,
  };
}

function toIsoDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function occupancyOptionLabel(value) {
  if (value === "staff_technician") return "Legacy salon staff path";
  if (value === "independent_owner") return "Independent operator";
  if (value === "renter_guest") return "Legacy guest path";
  return titleCaseLabel(value);
}

function occupancyOptionDetail(value) {
  if (value === "staff_technician") {
    return "Legacy records may still show this path, but new MyBoothShare rentals should stay in the independent-contractor lane.";
  }
  if (value === "independent_owner") {
    return "You operate as an independent contractor, keep your client ownership, and can work across multiple salon locations.";
  }
  if (value === "renter_guest") {
    return "Legacy guest records can still be displayed, but new launch-time checkout flows stay in the independent-operator lane.";
  }
  return "";
}

function warningToneClasses(level) {
  if (level === "high_risk") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  return "border-amber-200 bg-amber-50 text-amber-700";
}

export default function MarketplaceCheckoutPage() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [checkoutContext, setCheckoutContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedAddons, setSelectedAddons] = useState({});
  const [form, setForm] = useState(() => ({
    ...defaultBookingWindow(),
    pricing_type: "hourly",
    renter_name: user?.full_name || "",
    renter_email: user?.email || "",
    terms_version: "2026-04-21",
    occupancy_mode: "",
    accept_terms: false,
    acknowledge_client_ownership: false,
    acknowledge_operating_mode: false,
    acknowledge_payment_terms: false,
    acknowledge_platform_disclaimer: false,
    acknowledge_warnings: false,
  }));

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getPublicWorkspace(id, token || null)
      .then((data) => {
        if (active) setWorkspace(data);
      })
      .catch((err) => {
        if (active) setError(err.message || "Unable to load workspace.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, token]);

  useEffect(() => {
    let active = true;
    setContextLoading(true);
    getMarketplaceCheckoutContext(id, {
      token: token || null,
      occupancyMode: form.occupancy_mode || null,
    })
      .then((data) => {
        if (!active) return;
        const selectedMode =
          data?.selected_occupancy_mode === "staff_technician"
            ? "independent_owner"
            : data?.selected_occupancy_mode || "";
        setCheckoutContext(data);
        setForm((prev) =>
          prev.occupancy_mode === selectedMode
            ? prev
            : { ...prev, occupancy_mode: selectedMode }
        );
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail?.message ||
              err?.response?.data?.detail ||
              err.message ||
              "Unable to load checkout requirements."
          );
        }
      })
      .finally(() => {
        if (active) setContextLoading(false);
      });
    return () => {
      active = false;
    };
  }, [form.occupancy_mode, id, token]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      renter_name: user?.full_name || prev.renter_name,
      renter_email: user?.email || prev.renter_email,
    }));
  }, [user?.email, user?.full_name]);

  const addons = useMemo(
    () =>
      Array.isArray(workspace?.amenity_addons)
        ? workspace.amenity_addons.map(normalizeAddon)
        : [],
    [workspace?.amenity_addons]
  );

  const selectedAddonRows = useMemo(
    () => addons.filter((addon) => selectedAddons[addon.key]),
    [addons, selectedAddons]
  );

  const estimate = useMemo(() => {
    const start = new Date(form.start_at_utc);
    const end = new Date(form.end_at_utc);
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      !workspace
    ) {
      return null;
    }
    const seconds = (end.getTime() - start.getTime()) / 1000;
    if (seconds <= 0) {
      return null;
    }
    const durationHours = seconds / 3600;
    const durationDays = seconds / 86400;
    const dailyHours = Number(workspace.daily_hours || 8) || 8;
    let base = 0;
    if (form.pricing_type === "weekly" && workspace.price_per_week) {
      base =
        Math.max(1, Math.ceil(durationDays / 7)) *
        Number(workspace.price_per_week);
    } else if (form.pricing_type === "daily" && workspace.price_per_day) {
      base =
        Math.max(1, Math.ceil(durationDays)) * Number(workspace.price_per_day);
    } else if (form.pricing_type === "daily") {
      base =
        Math.max(1, Math.ceil(durationDays)) *
        dailyHours *
        Number(workspace.price_per_hour || 0);
    } else if (form.pricing_type === "weekly") {
      base =
        Math.max(1, Math.ceil(durationDays / 7)) *
        7 *
        dailyHours *
        Number(workspace.price_per_hour || 0);
    } else {
      base = durationHours * Number(workspace.price_per_hour || 0);
    }
    const addonsTotal = selectedAddonRows.reduce(
      (sum, addon) => sum + Number(addon.unit_price || 0),
      0
    );
    const cleaningFee = Number(workspace.cleaning_fee || 0);
    const deposit = Number(workspace.deposit_amount || 0);
    const rentalTotal = base + addonsTotal + cleaningFee;
    return {
      durationHours,
      base,
      addonsTotal,
      cleaningFee,
      deposit,
      dueNow: rentalTotal + deposit,
      dueLater: 0,
    };
  }, [
    form.end_at_utc,
    form.pricing_type,
    form.start_at_utc,
    selectedAddonRows,
    workspace,
  ]);

  const warnings = Array.isArray(checkoutContext?.warnings)
    ? checkoutContext.warnings
    : [];
  const agreementCompletionBlocked = Boolean(
    checkoutContext?.agreement_completion_blocked
  );

  const roleProgression = checkoutContext?.role_progression || {};
  const disclosures = Array.isArray(checkoutContext?.disclosures)
    ? checkoutContext.disclosures
    : [];
  const requiredAcknowledgementsComplete =
    Boolean(form.occupancy_mode) &&
    Boolean(form.acknowledge_client_ownership) &&
    Boolean(form.acknowledge_operating_mode) &&
    Boolean(form.acknowledge_payment_terms) &&
    Boolean(form.acknowledge_platform_disclaimer) &&
    Boolean(form.accept_terms) &&
    (warnings.length ? Boolean(form.acknowledge_warnings) : true);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const start_at_utc = toIsoDateTime(form.start_at_utc);
    const end_at_utc = toIsoDateTime(form.end_at_utc);
    if (!start_at_utc || !end_at_utc) {
      setError("Choose valid start and end times.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await createMarketplaceCheckout(
        {
          workspace_id: Number(id),
          start_at_utc,
          end_at_utc,
          pricing_type: form.pricing_type,
          renter_name: form.renter_name || null,
          renter_email: form.renter_email || null,
          occupancy_mode: form.occupancy_mode || null,
          selected_addons: selectedAddonRows.map((addon) => ({
            ...addon,
            line_total: Number(addon.unit_price || 0),
          })),
          accept_terms: Boolean(form.accept_terms),
          terms_version: form.terms_version,
          cancellation_policy_code: "workspace_policy",
          selected_agreement_template_id:
            checkoutContext?.agreement_template_id || null,
          agreement_version: checkoutContext?.agreement_version || null,
          warning_version: checkoutContext?.warning_version || null,
          acknowledge_client_ownership: Boolean(
            form.acknowledge_client_ownership
          ),
          acknowledge_operating_mode: Boolean(
            form.acknowledge_operating_mode
          ),
          acknowledge_payment_terms: Boolean(
            form.acknowledge_payment_terms
          ),
          acknowledge_platform_disclaimer: Boolean(
            form.acknowledge_platform_disclaimer
          ),
          acknowledge_warnings: warnings.length
            ? Boolean(form.acknowledge_warnings)
            : true,
        },
        token || null
      );
      if (response?.workspace_booking_id && token) {
        try {
          const rental = await getRentalByBooking(response.workspace_booking_id, token);
          if (rental?.id) {
            await acknowledgeRentalTerms(
              rental.id,
              {
                template_id: checkoutContext?.agreement_template_id || null,
                signature_name: form.renter_name || null,
                note: "Accepted through the marketplace checkout review step.",
                acknowledge_role_ownership_payment_terms: true,
              },
              token
            );
            if (warnings.length) {
              await acknowledgeRentalWarning(
                rental.id,
                {
                  note: "Reviewed current rental notices during marketplace checkout.",
                },
                token
              );
            }
          }
        } catch (_) {
          // The checkout snapshot already records acceptance state. Canonical rental acknowledgements
          // are best-effort here until every renter checkout path is fully rental-id-driven.
        }
      }
      if (response?.checkout_url) {
        window.location.assign(response.checkout_url);
        return;
      }
      setError("Checkout did not return a redirect URL.");
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err.message ||
          "Unable to start checkout."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketplaceShell>
      <section className="grid gap-6 lg:grid-cols-[1fr,0.86fr]">
        <article className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-[#2f3566] px-6 py-6 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
              Renter Checkout
            </p>
            <h1 className="mt-3 text-3xl font-semibold">
              {loading
                ? "Loading..."
                : getWorkspaceDisplayName(workspace) || "Workspace Rental Request"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-200 md:text-base">
              Confirm the operating relationship, ownership structure, agreement
              version, payment terms, and required acknowledgements before Stripe
              checkout starts.
            </p>
          </div>
          <form className="space-y-6 px-6 py-6" onSubmit={handleSubmit}>
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {typeof error === "string" ? error : "Unable to start checkout."}
              </div>
            ) : null}

            {checkoutContext?.strict_mode_enabled &&
            checkoutContext?.warning_state_level === "high_risk" ? (
              <div className="rounded-[1.5rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                Strict workspace compliance mode is enabled for this workspace provider. High-risk
                contradictions must be acknowledged before this request can be
                submitted.
              </div>
            ) : null}

            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Payment Summary
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Payment
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    Pay now
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Currency
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {(workspace?.currency || "cad").toUpperCase()}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Agreement
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {checkoutContext?.agreement_version || "Not configured"}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Operating Relationship
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                MyBoothShare is for independent contractors and independent professionals only. Employee and staff relationships stay in Ellyanna Booking.
              </p>
              <div className="mt-4 grid gap-3">
                {(checkoutContext?.occupancy_mode_options || [])
                  .filter((option) => option !== "staff_technician")
                  .map((option) => (
                  <label
                    key={option}
                    className={`rounded-[1.35rem] border px-4 py-4 text-sm ${
                      form.occupancy_mode === option
                        ? "border-rose-300 bg-rose-50 text-rose-700"
                        : "border-slate-200 bg-slate-50 text-slate-700"
                    }`}
                  >
                    <span className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="occupancy_mode"
                        value={option}
                        checked={form.occupancy_mode === option}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            occupancy_mode: event.target.value,
                            acknowledge_warnings: false,
                          }))
                        }
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-semibold">
                          {occupancyOptionLabel(option)}
                        </span>
                        <span className="mt-1 block leading-6 text-inherit/80">
                          {occupancyOptionDetail(option)}
                        </span>
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {(checkoutContext?.occupancy_mode_options || []).includes("staff_technician") ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Legacy salon staff rental paths still exist in the shared platform, but they are intentionally excluded from new MyBoothShare rental requests.
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Start
                </span>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.start_at_utc}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      start_at_utc: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  End
                </span>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.end_at_utc}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      end_at_utc: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Pricing Type
                </span>
                <select
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.pricing_type}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      pricing_type: event.target.value,
                    }))
                  }
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Terms Version
                </span>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.terms_version}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      terms_version: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Renter Name
                </span>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.renter_name}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      renter_name: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Renter Email
                </span>
                <input
                  type="email"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={form.renter_email}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      renter_email: event.target.value,
                    }))
                  }
                  required
                />
              </label>
            </div>

            {addons.length ? (
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Amenity Add-ons
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {addons.map((addon) => (
                    <label
                      key={addon.key}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
                    >
                      <span className="inline-flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedAddons[addon.key])}
                          onChange={(event) =>
                            setSelectedAddons((prev) => ({
                              ...prev,
                              [addon.key]: event.target.checked,
                            }))
                          }
                        />
                        <span>{addon.label}</span>
                      </span>
                      <span className="font-semibold text-slate-900">
                        {formatWorkspaceMoney(addon.unit_price)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Client Ownership
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Pricing model
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {titleCaseLabel(
                      checkoutContext?.compliance_profile_summary?.rent_model
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Client ownership
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {titleCaseLabel(checkoutContext?.client_ownership_mode)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Price control
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {titleCaseLabel(
                      checkoutContext?.compliance_profile_summary?.price_control
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Schedule control
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {titleCaseLabel(
                      checkoutContext?.compliance_profile_summary?.schedule_control
                    )}
                  </div>
                </div>
              </div>
              {roleProgression.owner_upgrade_required ? (
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                  Independent owner setup will still be required for this account.
                  Continue to{" "}
                  <Link className="font-semibold underline" to="/account/profile">
                    MyBoothShare profile
                  </Link>{" "}
                  after checkout to complete owner progression.
                </div>
              ) : null}
              {roleProgression.salon_join_request_required ? (
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                  This selection will preserve the workspace provider join-request workflow
                  when the rental request is submitted.
                </div>
              ) : null}
            </div>

            <RenterWarningsPanel
              title="Important Notices"
              warnings={warnings}
              strictModeEnabled={Boolean(checkoutContext?.strict_mode_enabled)}
              agreementCompletionBlocked={Boolean(
                agreementCompletionBlocked
              )}
              acknowledgementRequired={Boolean(warnings.length)}
            />

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Cancellation Policy
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">
                {workspace?.policy_set?.cancellation_policy_text ||
                  workspace?.policy_set?.house_rules_text ||
                  "This rental request follows the workspace provider's current cancellation and workspace policy."}
              </p>
            </div>

            <RenterAgreementAcceptanceSection
              values={form}
              warningsRequired={Boolean(warnings.length)}
              onToggle={(field, checked) =>
                setForm((prev) => ({
                  ...prev,
                  [field]: checked,
                }))
              }
            />

            <button
              type="submit"
              disabled={
                saving ||
                loading ||
                contextLoading ||
                agreementCompletionBlocked ||
                !requiredAcknowledgementsComplete
              }
              className="rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {agreementCompletionBlocked
                ? "Resolve blockers before checkout"
                : saving
                ? "Redirecting..."
                : `Continue To Checkout${
                    estimate ? ` ${formatWorkspaceMoney(estimate.dueNow)}` : ""
                  }`}
            </button>
          </form>
        </article>

        <aside className="space-y-6">
          <RenterAcceptanceSummaryCard
            workspaceName={getWorkspaceDisplayName(workspace)}
            workspaceType={workspace?.workspace_type}
            occupancyMode={
              form.occupancy_mode || checkoutContext?.selected_occupancy_mode
            }
            clientOwnershipMode={checkoutContext?.client_ownership_mode}
            agreementVersion={checkoutContext?.agreement_version}
            depositAmount={estimate ? estimate.deposit : null}
            dueNowAmount={estimate ? estimate.dueNow : null}
            dueLaterAmount={estimate ? estimate.dueLater : null}
            currency={workspace?.currency || "cad"}
            warningCount={warnings.length}
          />
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
              Arrangement Notes
            </p>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              <div className="font-semibold text-slate-900">Disclosures</div>
              <ul className="mt-3 space-y-2">
                {disclosures.map((item) => (
                  <li key={item}>{item}</li>
                ))}
                {!disclosures.length ? (
                  <li>This arrangement summary does not constitute legal advice.</li>
                ) : null}
              </ul>
            </div>
            {warnings.length ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-700">
                {warnings.length} warning{warnings.length === 1 ? "" : "s"} require acknowledgement before checkout.
              </div>
            ) : null}
            <Link
              to={workspace ? getWorkspaceListingPath(workspace) : `/workspaces/${id}`}
              className="mt-5 inline-flex rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            >
              Back to Listing
            </Link>
          </article>
        </aside>
      </section>
    </MarketplaceShell>
  );
}
